import React, { useEffect } from 'react';
import { Card } from '../../types/game';
import { theme } from '../../styles/theme';
import { CardCategoryIcon, getCategoryIconColor } from './CardCategoryIcon';

interface CardPickerProps {
  title: string;
  subtitle?: string;
  cards: Card[];
  onPick: (card: Card) => void;
  onCancel?: () => void;
  /** If true, show a "Skip" button instead of "Cancel" */
  allowSkip?: boolean;
}

/**
 * Full-screen modal for picking a card from a list.
 * Used by fieldcraft cards that require the player to choose a card
 * from hand, discard, or deck (Chain, Adapt, Copy, Recall, Stash,
 * Field Modify, Overclock, Improvise).
 */
export function CardPicker({ title, subtitle, cards, onPick, onCancel, allowSkip }: CardPickerProps) {
  // Escape / right-click cancels the picker if the caller provided an
  // onCancel handler. This matches the rest of the game where escape
  // always backs out of a selection flow.
  useEffect(() => {
    if (!onCancel) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onCancel();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onCancel]);

  return (
    <div style={styles.backdrop} onClick={onCancel}>
      <div style={styles.panel} onClick={e => e.stopPropagation()}>
        <div style={styles.header}>
          <span style={styles.title}>{title}</span>
          {onCancel && (
            <button style={styles.closeButton} onClick={onCancel}>
              {allowSkip ? 'SKIP' : '✕'}
            </button>
          )}
        </div>
        {subtitle && <div style={styles.subtitle}>{subtitle}</div>}
        <div style={styles.cardGrid}>
          {cards.map(card => {
            const catColor = getCategoryIconColor(card.category);
            return (
              <div
                key={card.id}
                style={{
                  ...styles.card,
                  borderColor: `${catColor}30`,
                }}
                onClick={() => onPick(card)}
                onMouseEnter={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = catColor;
                  (e.currentTarget as HTMLElement).style.background = `${catColor}10`;
                }}
                onMouseLeave={e => {
                  (e.currentTarget as HTMLElement).style.borderColor = `${catColor}30`;
                  (e.currentTarget as HTMLElement).style.background = theme.colors.surface;
                }}
              >
                <div style={styles.cardTop}>
                  <div style={styles.cardCost}>{card.apCost}</div>
                  <div style={styles.cardIcon}>
                    <CardCategoryIcon category={card.category} size={14} />
                  </div>
                </div>
                <div style={styles.cardName}>{card.name}</div>
                <div style={{ ...styles.cardCategory, color: catColor }}>{card.category.toUpperCase()}</div>
                {card.movementRange && (
                  <div style={styles.cardStat}>{card.movementRange}m</div>
                )}
                {/* Keyword badges — positioned below the icon to avoid overlap */}
                <div style={styles.badgeRow}>
                  {card.isBurn && <span style={styles.burnBadge}>BURN</span>}
                  {card.isChain && <span style={styles.chainBadge}>CHAIN</span>}
                </div>
                <div style={styles.cardDesc}>{card.description}</div>
              </div>
            );
          })}
        </div>
        {cards.length === 0 && (
          <div style={styles.emptyMessage}>No cards available.</div>
        )}
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  backdrop: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 2000,
    backdropFilter: 'blur(4px)',
  },
  panel: {
    background: theme.colors.background,
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '8px',
    maxWidth: '700px',
    width: '90%',
    maxHeight: '80vh',
    overflow: 'auto',
    padding: '20px',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  title: {
    fontFamily: theme.fonts.mono,
    fontSize: '14px',
    fontWeight: 700,
    color: theme.colors.accentGreen,
    letterSpacing: '2px',
    textTransform: 'uppercase' as const,
  },
  closeButton: {
    background: 'rgba(255, 255, 255, 0.05)',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: '4px',
    color: theme.colors.textSecondary,
    fontFamily: theme.fonts.mono,
    fontSize: '11px',
    padding: '4px 10px',
    cursor: 'pointer',
    letterSpacing: '1px',
  },
  subtitle: {
    fontFamily: theme.fonts.mono,
    fontSize: '11px',
    color: theme.colors.textSecondary,
    marginBottom: '16px',
    lineHeight: '1.4',
  },
  cardGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
    gap: '10px',
  },
  card: {
    background: theme.colors.surface,
    border: '1px solid',
    borderRadius: '6px',
    padding: '12px',
    cursor: 'pointer',
    transition: 'border-color 0.15s, background 0.15s',
    position: 'relative' as const,
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '6px',
  },
  cardCost: {
    background: '#ffcc00',
    color: '#000',
    fontFamily: theme.fonts.mono,
    fontWeight: 700,
    fontSize: '11px',
    width: '20px',
    height: '20px',
    borderRadius: '4px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardIcon: {
    opacity: 0.7,
  },
  cardName: {
    fontFamily: theme.fonts.mono,
    fontSize: '12px',
    fontWeight: 700,
    color: theme.colors.textPrimary,
    marginBottom: '2px',
  },
  cardCategory: {
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    letterSpacing: '1px',
    marginBottom: '6px',
  },
  cardStat: {
    fontFamily: theme.fonts.mono,
    fontSize: '10px',
    color: theme.colors.accentBlue,
    marginBottom: '4px',
  },
  cardDesc: {
    fontFamily: theme.fonts.mono,
    fontSize: '9px',
    color: theme.colors.textSecondary,
    lineHeight: '1.4',
  },
  badgeRow: {
    display: 'flex',
    gap: '4px',
    marginBottom: '4px',
  },
  burnBadge: {
    background: 'rgba(255, 80, 50, 0.2)',
    color: '#ff5032',
    fontFamily: theme.fonts.mono,
    fontSize: '8px',
    fontWeight: 700,
    letterSpacing: '1px',
    padding: '1px 5px',
    borderRadius: '3px',
    border: '1px solid rgba(255, 80, 50, 0.3)',
  },
  chainBadge: {
    background: 'rgba(68, 170, 255, 0.2)',
    color: '#44aaff',
    fontFamily: theme.fonts.mono,
    fontSize: '8px',
    fontWeight: 700,
    letterSpacing: '1px',
    padding: '1px 5px',
    borderRadius: '3px',
    border: '1px solid rgba(68, 170, 255, 0.3)',
  },
  emptyMessage: {
    fontFamily: theme.fonts.mono,
    fontSize: '12px',
    color: theme.colors.textDim,
    textAlign: 'center' as const,
    padding: '30px',
  },
};
